/**
 * Inline keyword-gap form on /kwfinder/features/keyword-gap-analysis-tool/.
 *
 * Replaces the previous MFE-based React component with a vanilla-JS form that
 * submits directly to app.mangools.com/kwfinder/keyword-gap-results with the
 * canonical query params the kwfinder app expects.
 *
 * Spec mapping (Notion KWF Keyword Gap v.2 sub-page 2656ae8ffb80819c9ac7cf16211fcdd1):
 *   - Scenario 1: 1 competitor → submit → keyword-gap-results
 *   - Scenario 2: + Add up to 4 competitors → upgrade modal (anonymous + free)
 *                 or expand form (logged-in basic+)
 *
 * window.MangoolsContext.user is the cross-MFE auth signal populated by the
 * landing host scripts (mangools-commons/auth). isLoggedIn reads .email,
 * isPaidPlan reads .plan ∈ basic|premium|agency|entry|custom.
 */

const UrlService = require('mangools-commons/lib/services/UrlService').default;

const PAID_PLANS = ['basic', 'premium', 'agency', 'entry', 'custom'];
const MAX_TOTAL_SLOTS = 5; // primary + up to 4 competitors
const PRICING_URL = 'https://mangools.com/plans-and-pricing?ref=msg-gap-upsell';
const REF_GAP_REDIRECT = 'msg-app-keyword-gap'; // per spec Scenario 1.1 callout

const classify = UrlService.classifyInput;

function kindToLabel(kind) {
    if (kind === 'domain') return 'Domain';
    if (kind === 'subdomain') return 'Subdomain';
    if (kind === 'url') return 'URL';
    return null;
}

function updatePillForInput(input) {
    const wrapper = input.closest('.kw-gap-input-wrapper');
    if (!wrapper) return;
    const pill = wrapper.querySelector('.kw-gap-pill');
    const labelEl = pill && pill.querySelector('.kw-gap-pill-label');
    if (!pill || !labelEl) return;

    const value = (input.value || '').trim();
    if (!value) {
        pill.hidden = true;
        return;
    }

    const kind = classify(value);
    if (kind === null) {
        pill.hidden = true;
        return;
    }
    labelEl.textContent = kindToLabel(kind);
    pill.hidden = false;
}

function attachPillListener(input) {
    if (!input || input.dataset.kwGapPillBound === '1') return;
    input.dataset.kwGapPillBound = '1';
    input.addEventListener('input', function () {
        input.setCustomValidity('');
        updatePillForInput(input);
        const form = input.form;
        if (form) clearError(form);
    });
    input.addEventListener('change', function () { updatePillForInput(input); });
    input.addEventListener('blur', function () {
        const form = input.form;
        if (!form) return;
        if (isFormMixed(form)) {
            showError(form, 'Please choose between analysing domains/subdomains and URLs. Currently, you can\'t analyze a keyword gap between a domain/subdomain and a URL.');
        }
    });
    updatePillForInput(input);
}

function isFormMixed(form) {
    const fd = new FormData(form);
    const all = [(fd.get('primary') || '').toString()]
        .concat(fd.getAll('competitors[]').map((v) => v.toString()))
        .map((v) => v.trim())
        .filter(Boolean);
    if (all.length < 2) return false;
    const kinds = all.map(classify).filter((k) => k !== null);
    const hasUrl = kinds.indexOf('url') !== -1;
    const hasNonUrl = kinds.indexOf('domain') !== -1 || kinds.indexOf('subdomain') !== -1;
    return hasUrl && hasNonUrl;
}

function showUrlTypeInfoModal() {
    if (window.UIkit && window.UIkit.modal) {
        window.UIkit.modal('#kw-gap-url-type-info-modal').show();
        return;
    }
    const modal = document.getElementById('kw-gap-url-type-info-modal');
    if (modal) {
        modal.classList.add('uk-open');
        modal.style.display = 'block';
    }
}

function deriveMode(kinds) {
    const valid = kinds.filter((k) => k !== null);
    if (!valid.length) return 'invalid';
    const hasUrl = valid.indexOf('url') !== -1;
    const hasNonUrl = valid.indexOf('domain') !== -1 || valid.indexOf('subdomain') !== -1;
    if (hasUrl && hasNonUrl) return 'mixed';
    return hasUrl ? 'pages' : 'domains';
}

function isLoggedIn() {
    var ctx = window.MangoolsContext;
    return Boolean(ctx && ctx.user && ctx.user.email);
}

function isPaidPlan() {
    var ctx = window.MangoolsContext;
    if (!ctx || !ctx.user || typeof ctx.user.plan !== 'string') return false;
    return PAID_PLANS.indexOf(ctx.user.plan.toLowerCase()) !== -1;
}

const TOOLTIP_ICON_SVG =
    '<span class="kw-gap-tooltip-icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" focusable="false" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>' +
            '<line x1="12" y1="9" x2="12" y2="13"></line>' +
            '<line x1="12" y1="17" x2="12.01" y2="17"></line>' +
        '</svg>' +
    '</span>';

function showError(form, msg) {
    const errEl = form.querySelector('#kw-gap-error');
    const wrapper = form.querySelector('#kw-gap-error-wrapper') || errEl;
    if (!errEl || !wrapper) return;
    const safeMsg = document.createElement('span');
    safeMsg.textContent = msg;
    errEl.innerHTML = TOOLTIP_ICON_SVG + safeMsg.outerHTML;
    wrapper.classList.remove('uk-hidden');
}

function clearError(form) {
    const errEl = form.querySelector('#kw-gap-error');
    const wrapper = form.querySelector('#kw-gap-error-wrapper') || errEl;
    if (!errEl || !wrapper) return;
    errEl.textContent = '';
    wrapper.classList.add('uk-hidden');
}

function clearValidity(form) {
    const inputs = form.querySelectorAll('input[name]');
    for (let i = 0; i < inputs.length; i += 1) {
        inputs[i].setCustomValidity('');
    }
}

function getAppHost() {
    const host = window.location.hostname;
    if (host.indexOf('mnglsbeta') !== -1) return 'https://app.mnglsbeta.com';
    if (host.indexOf('localhost') !== -1) return 'http://localhost:8000';
    return 'https://app.mangools.com';
}

function buildResultsUrl(primary, competitors, locationId, mode) {
    const params = new URLSearchParams();
    params.set('mode', mode);
    params.set('location_id', locationId || '0');
    if (mode === 'domains') {
        params.set('domain', primary);
        params.set('competitors', competitors.join(','));
    } else if (mode === 'pages') {
        params.set('exclude_pages', primary);
        params.set('pages', competitors.join(','));
    }
    params.set('ref', REF_GAP_REDIRECT);
    return getAppHost() + '/kwfinder/keyword-gap-results?' + params.toString();
}

function handleSubmit(event) {
    const form = event.currentTarget;
    event.preventDefault();
    clearError(form);
    clearValidity(form);

    const fd = new FormData(form);
    const primary = (fd.get('primary') || '').toString().trim();
    const competitors = fd.getAll('competitors[]').map((v) => v.toString().trim()).filter(Boolean);
    const locationId = (fd.get('location_id') || '0').toString();

    const primaryEl = form.querySelector('input[name="primary"]');
    const competitorEls = form.querySelectorAll('input[name="competitors[]"]');

    if (!primary) {
        if (primaryEl) {
            primaryEl.setCustomValidity('Domain/subdomain or a URL is required');
            primaryEl.reportValidity();
        }
        return;
    }
    if (!competitors.length) {
        if (competitorEls.length) {
            competitorEls[0].setCustomValidity('At least one competitor is required');
            competitorEls[0].reportValidity();
        }
        return;
    }

    const allInputs = [primary].concat(competitors);
    const kinds = allInputs.map(classify);
    const invalidIdx = kinds.indexOf(null);
    if (invalidIdx !== -1) {
        const target = invalidIdx === 0 ? primaryEl : competitorEls[invalidIdx - 1];
        if (target) {
            target.setCustomValidity('Please insert a domain/subdomain or a URL');
            target.reportValidity();
        }
        return;
    }

    const mode = deriveMode(kinds);
    if (mode === 'mixed') {
        showError(form, 'Please choose between analysing domains/subdomains and URLs. Currently, you can\'t analyze a keyword gap between a domain/subdomain and a URL.');
        return;
    }
    if (mode === 'invalid') {
        if (primaryEl) {
            primaryEl.setCustomValidity('Please insert a domain/subdomain or a URL');
            primaryEl.reportValidity();
        }
        return;
    }

    window.location.assign(buildResultsUrl(primary, competitors, locationId, mode));
}

function buildCompetitorRow(index) {
    const row = document.createElement('div');
    row.className = 'mg-searchform-inner uk-flex uk-flex-wrap uk-width-1-1 uk-position-relative mg-margin-t-15';
    row.innerHTML =
        '<div class="kw-gap-input-wrapper uk-width-1-1">' +
            '<input aria-label="Competitor ' + (index + 1) + '" autocapitalize="none" ' +
            'class="mg-searchform-s uk-width-1-1 font-14" name="competitors[]" ' +
            'placeholder="A competitor\'s domain/subdomain or a URL" ' +
            'spellcheck="false" type="text" value="">' +
            '<span class="kw-gap-pill" data-for="competitors[]" hidden>' +
                '<span class="kw-gap-pill-label"></span>' +
                '<button type="button" class="kw-gap-pill-info-trigger" aria-label="What input types are supported?">' +
                    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.9 12.4H7.1V7h1.8v5.4zM8 5.8a1 1 0 1 1 0-2.1 1 1 0 0 1 0 2z"/></svg>' +
                '</button>' +
            '</span>' +
        '</div>';
    return row;
}

function handleAddCompetitor(event) {
    if (event.target && typeof event.target.closest === 'function' && event.target.closest('.kw-gap-info-tooltip')) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }
    event.preventDefault();
    const container = document.getElementById('kw-gap-competitor-rows');
    if (!container) return;

    if (!isLoggedIn() || !isPaidPlan()) {
        const modal = document.getElementById('kw-gap-upgrade-modal');
        if (modal && window.UIkit && window.UIkit.modal) {
            window.UIkit.modal('#kw-gap-upgrade-modal').show();
        } else if (modal) {
            modal.classList.add('uk-open');
            modal.style.display = 'block';
        } else {
            window.location.assign(PRICING_URL);
        }
        return;
    }

    const currentCount = container.querySelectorAll('input[name="competitors[]"]').length;
    const slotsToAdd = (MAX_TOTAL_SLOTS - 1) - currentCount;
    if (slotsToAdd <= 0) return;

    for (let i = 0; i < slotsToAdd; i += 1) {
        const idx = currentCount + i;
        const newRow = buildCompetitorRow(idx);
        container.appendChild(newRow);
        const newInput = newRow.querySelector('input[name="competitors[]"]');
        if (newInput) attachPillListener(newInput);
    }

    const addBtn = document.getElementById('kw-gap-add-competitor');
    if (addBtn) addBtn.style.display = 'none';
}

function init() {
    const form = document.getElementById('kw-gap-form');
    if (!form) return;
    form.addEventListener('submit', handleSubmit);
    const addBtn = document.getElementById('kw-gap-add-competitor');
    if (addBtn) addBtn.addEventListener('click', handleAddCompetitor);

    const pillInputs = form.querySelectorAll('input[name="primary"], input[name="competitors[]"]');
    for (let i = 0; i < pillInputs.length; i += 1) {
        attachPillListener(pillInputs[i]);
    }

    form.addEventListener('click', function (event) {
        const trigger = event.target && typeof event.target.closest === 'function'
            ? event.target.closest('.kw-gap-pill-info-trigger')
            : null;
        if (trigger) {
            event.preventDefault();
            event.stopPropagation();
            showUrlTypeInfoModal();
        }
    });
}

module.exports = { init, classify, deriveMode };
